import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, LayoutTemplate, Save, Loader2 } from "lucide-react";

const BILLING_OPTIONS = [
  { value: "base", label: "Alap (feladás + napi)" },
  { value: "one_time", label: "Egyszeri kiegészítő" },
  { value: "weekly", label: "Heti kiegészítő" },
  { value: "daily_extra", label: "Extra napi (statisztika)" },
  { value: "none", label: "Nincs automatikus ár" },
];

const ADDON_KEYS = [
  { value: "", label: "— nincs —" },
  { value: "viewer_360", label: "360° körbeforgatás" },
  { value: "ai_photo_bg", label: "AI háttércsere" },
  { value: "ai_customer_support", label: "AI ügyfélszolgálat" },
  { value: "list_boost", label: "Kiemelés" },
  { value: "priority_ranking", label: "TOP rangsor" },
  { value: "meta_ads", label: "Meta hirdetés" },
  { value: "detailed_stats", label: "Részletes stat" },
];

export function AdminSellerServicesEditor({ page, onChange, onSave, saving = false }) {
  const p = page || {};
  const setPage = (patch) => onChange({ ...(page || {}), ...patch });
  const sections = [...(p.sections || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );

  const setSections = (next) => setPage({ sections: next });

  const updateSection = (i, patch) => {
    const next = [...sections];
    next[i] = { ...next[i], ...patch };
    setSections(next);
  };

  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    const orderA = next[i].sort_order ?? i * 10;
    const orderB = next[j].sort_order ?? j * 10;
    next[i] = { ...next[i], sort_order: orderB };
    next[j] = { ...next[j], sort_order: orderA };
    setSections(next);
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid="admin-seller-services">
      <div className="flex items-center gap-2 mb-2">
        <LayoutTemplate className="w-4 h-4 text-electric" strokeWidth={1.5} />
        <h3 className="font-heading text-lg">Szolgáltatások oldal (/szolgaltatasok)</h3>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <p className="text-xs text-slate-500 max-w-xl">
          A nyilvános „Szolgáltatások” fül tartalma. Üres című szekció nem mentődik. A • sorok listaként jelennek meg.
          A lenti globális mentés is elmenti — de itt külön is menthetsz.
        </p>
        {onSave && (
          <button
            type="button"
            data-testid="admin-svc-save"
            disabled={saving}
            onClick={() => onSave?.({ ...p, sections })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium shrink-0"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Szolgáltatások mentése
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Field label="Főcím" testid="admin-svc-hero-title">
          <input
            value={p.hero_title || ""}
            onChange={(e) => setPage({ hero_title: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="CTA gomb (elsődleges)" testid="admin-svc-cta-primary-label">
          <input
            value={p.cta_primary_label || ""}
            onChange={(e) => setPage({ cta_primary_label: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="CTA útvonal (elsődleges)" testid="admin-svc-cta-primary-path" className="md:col-span-2">
          <input
            value={p.cta_primary_path || ""}
            onChange={(e) => setPage({ cta_primary_path: e.target.value })}
            placeholder="/fiok/uj-hirdetes"
            className={inputCls}
          />
        </Field>
        <Field label="CTA gomb (másodlagos)" testid="admin-svc-cta-secondary-label">
          <input
            value={p.cta_secondary_label || ""}
            onChange={(e) => setPage({ cta_secondary_label: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="CTA útvonal (másodlagos)" testid="admin-svc-cta-secondary-path">
          <input
            value={p.cta_secondary_path || ""}
            onChange={(e) => setPage({ cta_secondary_path: e.target.value })}
            placeholder="/kreditek"
            className={inputCls}
          />
        </Field>
        <Field label="Bevezető szöveg" testid="admin-svc-hero-lead" className="md:col-span-2">
          <textarea
            rows={3}
            value={p.hero_lead || ""}
            onChange={(e) => setPage({ hero_lead: e.target.value })}
            className={`${inputCls} resize-none`}
          />
        </Field>
      </div>

      <div className="space-y-4">
        {sections.map((sec, i) => (
          <div key={sec.id || i} className="bg-white/[0.03] rounded-xl p-4 border border-white/5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Szekció #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Fel"
                  onClick={() => moveSection(i, -1)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="Le"
                  onClick={() => moveSection(i, 1)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`admin-svc-remove-${i}`}
                  onClick={() => setSections(sections.filter((_, x) => x !== i))}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 ml-1"
                >
                  <Trash2 className="w-3 h-3" /> Törlés
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Cím">
                <input
                  data-testid={`admin-svc-title-${i}`}
                  value={sec.title || ""}
                  onChange={(e) => updateSection(i, { title: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Rövid alcím (tagline)">
                <input
                  value={sec.tagline || ""}
                  onChange={(e) => updateSection(i, { tagline: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Ár típus">
                <select
                  value={sec.billing || "none"}
                  onChange={(e) => updateSection(i, { billing: e.target.value })}
                  className={inputCls}
                >
                  {BILLING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Kiegészítő kulcs (ár mutatáshoz)">
                <select
                  value={sec.addon_key || ""}
                  onChange={(e) => updateSection(i, { addon_key: e.target.value })}
                  className={inputCls}
                >
                  {ADDON_KEYS.map((o) => (
                    <option key={o.value || "none"} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Részletes szöveg">
              <textarea
                data-testid={`admin-svc-body-${i}`}
                rows={6}
                value={sec.body || ""}
                onChange={(e) => updateSection(i, { body: e.target.value })}
                placeholder="Bekezdések között üres sor. Felsorolás: • sor elején."
                className={`${inputCls} resize-y min-h-[120px] font-mono text-[13px]`}
              />
            </Field>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={sec.published !== false}
                onChange={(e) => updateSection(i, { published: e.target.checked })}
                className="accent-[#2563EB]"
              />
              Megjelenik a nyilvános oldalon
            </label>
          </div>
        ))}
      </div>

      <button
        type="button"
        data-testid="admin-svc-add-section"
        onClick={() =>
          setSections([
            ...sections,
            {
              id: `svc_${Date.now()}`,
              title: "",
              tagline: "",
              body: "",
              addon_key: "",
              billing: "none",
              published: true,
              sort_order: (sections.length + 1) * 10,
            },
          ])
        }
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full glass hover:bg-white/10"
      >
        <Plus className="w-3.5 h-3.5" /> Új szekció
      </button>
    </div>
  );
}

const inputCls =
  "w-full bg-anthracite-900 border border-white/10 rounded-lg p-2.5 text-sm focus:border-electric outline-none";

function Field({ label, children, testid, className = "" }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
